import { Box, IconButton, Stack, Typography } from '@atgfr8/atg-ui-tailwind';
import {
  getRateConActionIcon,
  getRateConStatusClass,
  rateConStatusBgAndTextColor,
} from '@common/order/carrier/secureRateCon';
import { ChevronDownIcon, ChevronUpIcon } from '@atgfr8/icons';
import { SecureRateConViewModels } from '@typeDefinitions/loadTypes';
import { formatPhoneNumber } from '@utils/phoneNumberFormat';
import { DateTime } from 'luxon';
import { Component, createSignal, For, Show } from 'solid-js';

export { rateConStatusBgAndTextColor };

export const RateConfirmationActivityHistory: Component<{
  secureRateConViewModels?: SecureRateConViewModels[] | null;
  carrierName?: string;
}> = (props) => {
  const Content = (props: { item: SecureRateConViewModels }) => {
    const [expanded, setExpanded] = createSignal(false);

    return (
      <Box class="border! border-[#b7b8b9]! rounded-xl ">
        <Box
          class={`bg-[#ECEFF1] p-2 flex justify-between cursor-pointer  items-center ${
            expanded()
              ? 'rounded-t-xl  border-b! border-[#b7b8b9]!'
              : 'rounded-xl'
          }`}
          onClick={() => {
            setExpanded(!expanded());
          }}
        >
          <Box class="flex gap-2 items-center ">
            <span
              class={`${getRateConStatusClass(
                props.item.status,
              )} px-2 py-1 rounded-full capitalize font-semibold text-[13px]`}
            >
              {props.item.status.replaceAll('_', ' ')}
            </span>
            <Typography
              variant="body1"
              component="span"
              class="break-all text-sm! font-roboto! text-[#000000DE]! font-medium!"
            >
              {props.item.carrierName ?? 'n/a'}
            </Typography>
          </Box>
          <Box class="flex gap-2 items-center text-sm font-roboto font-normal">
            Created{' '}
            <span class="text-[#000000] font-medium">
              {DateTime.fromISO(props.item.createdDate)
                .toLocal()
                .toFormat('MM/dd/yyyy hh:mm a')}
            </span>
            <IconButton dataTestId="rate-confirmation-history-toggle-button">
              {expanded() ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </IconButton>
          </Box>
        </Box>
        <Box
          class={`transition-all duration-300 ease-in-out overflow-hidden ${
            expanded() ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <Stack spacing={1.5} class="p-3">
            <For
              fallback={
                <Box class=" text-center text-sm font-semibold">
                  No Rate Confirmation Activity Action found.
                </Box>
              }
              each={props.item.secureRateConHistory ?? []}
            >
              {(history) => {
                const actionIcon = getRateConActionIcon(history.actionType);

                return (
                  <Box class="rounded-xl border-[1px] border-[#026EA180]">
                    <Box class="bg-[#F2F6F8] p-2 flex gap-2 items-center rounded-t-xl border-b-[1px] border-[#026EA180]">
                      <img
                        src={actionIcon}
                        alt={history.actionType}
                        class="size-5!"
                      />
                      <span class="text-[#000000DE] text-base font-roboto font-medium capitalize">
                        {history.actionType.replaceAll('_', ' ')}
                      </span>
                      <span class="text-[#000000DE]! text-sm! font-roboto">
                        {DateTime.fromISO(history.occurredAtUtc)
                          .toLocal()
                          .toFormat('MM/dd/yyyy hh:mm a')}
                      </span>
                    </Box>
                    <Box
                      display="flex"
                      alignItems="flex-start"
                      justifyContent="space-evenly"
                      class="p-3"
                      gap={1.5}
                    >
                      <Box flex={1}>
                        <Typography
                          variant="body1"
                          component="p"
                          class="text-xs! font-semibold! font-roboto! text-[#123B50]! mb-1!"
                        >
                          User
                        </Typography>
                        <Typography
                          variant="body1"
                          component="p"
                          class="break-all text-sm! font-roboto! text-[#000000DE]!"
                        >
                          {history.fullName}
                        </Typography>
                      </Box>
                      <Show when={Boolean(history.email)}>
                        <Box flex={1}>
                          <Typography
                            variant="body1"
                            component="p"
                            class="text-xs! font-semibold! font-roboto! text-[#123B50]! mb-1!"
                          >
                            Email
                          </Typography>
                          <a href={`mailto:${history.email}`}>
                            <Typography
                              variant="body1"
                              component="p"
                              class="break-all text-[#026EA1] underline text-sm! font-roboto!"
                            >
                              {history.email}
                            </Typography>
                          </a>
                        </Box>
                      </Show>
                      <Show when={Boolean(history.phone)}>
                        <Box flex={0.65}>
                          <Typography
                            variant="body1"
                            component="p"
                            class="text-xs! font-semibold! font-roboto! text-[#123B50]! mb-1!"
                          >
                            Phone
                          </Typography>
                          <Typography
                            variant="body1"
                            component="p"
                            class="break-all text-sm! font-roboto! text-[#000000DE]!"
                          >
                            {formatPhoneNumber(history.phone)}
                          </Typography>
                        </Box>
                      </Show>
                    </Box>
                  </Box>
                );
              }}
            </For>
          </Stack>
        </Box>
      </Box>
    );
  };
  return (
    <Stack
      spacing={1.5}
      sx={{
        'max-height': '80vh',
        'overflow-y': 'auto',
      }}
    >
      {props.secureRateConViewModels &&
      props.secureRateConViewModels.length > 0 ? (
        <For each={props.secureRateConViewModels}>
          {(item) => {
            return <Content item={item} />;
          }}
        </For>
      ) : (
        <Box class="text-center text-lg font-semibold border border-[#b7b8b9] rounded-xl p-3 bg-[#ECEFF1]">
          <Typography
            variant="body1"
            component="p"
            class="text-[#000000DE]! font-roboto! font-medium!"
          >
            No Rate Confirmation Activity History found.
          </Typography>
        </Box>
      )}
    </Stack>
  );
};
